/**
 * Local (client-side) text moderation shared by all UgcApi variants.
 *
 * Runs the same procedural profanity filter the venus server runs, so games
 * can pre-flight user text without a network round trip. This is a UX
 * pre-filter only — the server still enforces its own moderation on submit.
 *
 * SERVER PARITY CONTRACT: the policies below mirror
 * server/cloud-run/src/services/profanityPolicies.ts, and package.json pins
 * glin-profanity to the exact version the server lockfiles resolve (word-list
 * drift across versions would break client/server parity). Both are enforced
 * by tests/UgcCheckText.parity.test.ts and tests/UgcCheckText.lockstep.test.ts
 * — bump the SDK pin and the server packages together.
 */
import type { UgcCheckTextOptions, UgcCheckTextResult } from './UgcApi'

interface ProfanityPolicy {
  allLanguages?: boolean
  languages?: 'english'[]
  allowObfuscatedMatch: boolean
  wordBoundaries: boolean
  fuzzyToleranceLevel: number
  /** Dictionary matches to un-flag after the glin check. glin's
   *  checkProfanity wrapper overwrites FilterConfig.ignoreWords with its own
   *  global whitelist, so callers post-filter matches against this list. */
  ignoreWords?: string[]
}

/**
 * All 23 languages, obfuscation + fuzzy matching on. Matches the server's
 * policy for UGC, comments, profiles, and game tags.
 */
export const STRICT_PROFANITY_POLICY: ProfanityPolicy = Object.freeze({
  allLanguages: true,
  allowObfuscatedMatch: true,
  wordBoundaries: true,
  fuzzyToleranceLevel: 0.6,
})

/**
 * Exact-match English only. Matches the server's policy for AI generation
 * prompts. The fuzzy level is above glin's 1.0 score ceiling, which makes
 * fuzzy matching impossible and leaves only exact regex matches.
 */
export const LENIENT_PROFANITY_POLICY: ProfanityPolicy = Object.freeze({
  languages: ['english'] as 'english'[],
  allowObfuscatedMatch: false,
  wordBoundaries: true,
  fuzzyToleranceLevel: 1.1,
  // Mild entries in glin's English list that legitimately appear in creative
  // prompts. Mirrors the server; explicitly sexual terms stay blocked.
  ignoreWords: ['sexy', 'suck', 'butt', 'bastard'],
})

// Lazy-loaded: glin's 23-language word lists are ~85 KB and games that never
// moderate shouldn't pay for them at SDK boot.
let glinModulePromise: Promise<typeof import('glin-profanity')> | null = null

function loadGlin(): Promise<typeof import('glin-profanity')> {
  if (!glinModulePromise) {
    glinModulePromise = import('glin-profanity')
  }
  return glinModulePromise
}

// glin warns this on every call with allowObfuscatedMatch — i.e. every
// strict-policy check. Suppress just that message so games moderating
// per-keystroke don't get console spam.
const GLIN_OBFUSCATION_WARN =
  '[Glin-Profanity] Obfuscated match enabled → wordBoundaries will be ignored internally.'

function withGlinWarnSuppressed<T>(fn: () => T): T {
  const originalWarn = console.warn
  console.warn = (...args: unknown[]) => {
    if (args[0] === GLIN_OBFUSCATION_WARN) return
    originalWarn.apply(console, args)
  }
  try {
    return fn()
  } finally {
    console.warn = originalWarn
  }
}

/**
 * Shared implementation behind UgcApi.checkTextAsync. Fail-open: any internal
 * glin error (load or check) resolves to a clean result, matching the
 * server's behavior for its local profanity check. Never rejects.
 */
export async function checkText(
  text: string,
  options?: UgcCheckTextOptions,
): Promise<UgcCheckTextResult> {
  try {
    const { checkProfanity } = await loadGlin()
    const policy =
      options?.policy === 'lenient' ? LENIENT_PROFANITY_POLICY : STRICT_PROFANITY_POLICY
    const result = withGlinWarnSuppressed(() =>
      checkProfanity(text, {
        allLanguages: policy.allLanguages,
        languages: policy.languages,
        allowObfuscatedMatch: policy.allowObfuscatedMatch,
        wordBoundaries: policy.wordBoundaries,
        fuzzyToleranceLevel: policy.fuzzyToleranceLevel,
        customWords: options?.customWords,
      }),
    )
    const matched = result.containsProfanity ? result.profaneWords : []
    const ignore = new Set((policy.ignoreWords ?? []).map((word) => word.toLowerCase()))
    // A game's own customWords ban wins over the platform allowlist.
    const custom = new Set((options?.customWords ?? []).map((word) => word.toLowerCase()))
    const profaneWords = matched.filter(
      (word) => custom.has(word.toLowerCase()) || !ignore.has(word.toLowerCase()),
    )
    return { clean: profaneWords.length === 0, profaneWords }
  } catch {
    return { clean: true, profaneWords: [] }
  }
}
